<script setup lang="ts">
import { getProductRoute } from "@shopware-pwa/helpers-next";
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import { ref, computed, unref } from "vue";
import type { ComputedRef } from "vue";
import { defu } from "defu";
import { useProductConfigurator } from "#imports";
import { useRouter } from "vue-router";
import type { Schemas } from "#shopware";

const props = withDefaults(
  defineProps<{
    allowRedirect?: boolean;
  }>(),
  {
    allowRedirect: true,
  }
);

type Translations = {
  product: {
    chooseA: string;
  };
};

let translations: Translations = {
  product: {
    chooseA: "Choose a",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const emit = defineEmits<{
  (e: "change", selected: Partial<Schemas["Product"]> | undefined): void;
}>();
const isLoading = ref<boolean>();
const router = useRouter();
const {
  handleChange,
  getOptionGroups,
  getSelectedOptions,
  findVariantForSelectedOptions,
} = useProductConfigurator();

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const selectedOptions: ComputedRef = computed(() =>
  Object.values(unref(getSelectedOptions))
);
const isOptionSelected = (optionId: string) =>
  Object.values(getSelectedOptions.value).includes(optionId);

const onHandleChange = async () => {
  isLoading.value = true;
  const variantFound = await findVariantForSelectedOptions(
    unref(selectedOptions)
  );

  const selectedOptionsVariantPath = getProductRoute(variantFound);
  if (props.allowRedirect && selectedOptionsVariantPath) {
    try {
      await router.push(
        formatLink({
          path: selectedOptionsVariantPath.path,
          query: { size: "change" },
        })
      );
    } catch {
      console.error("incorrect URL", selectedOptionsVariantPath);
    }
  } else {
    emit("change", variantFound);
  }
  isLoading.value = false;
};

const sortByPosition = (options: any) => {
  let newOptions = Object.assign([], options);
  return newOptions.sort((a: any, b: any) =>
    a.position > b.position ? 1 : -1
  );
};
</script>

<template>
  <div class="product-configurator">
    <div
      v-if="isLoading"
      class="absolute inset-0 flex items-center justify-center z-10 bg-white/75"
    >
      <div
        data-testid="loading"
        class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-gray-500"
      />
    </div>
    <div
      v-for="optionGroup in getOptionGroups"
      :key="optionGroup.id"
      class="mb-5"
    >
      <h6 class="mb-[10px] normal-case flex items-center justify-between">
        {{ optionGroup.name }}
      </h6>
      <div class="flex flex-wrap gap-2 color-box-wrap mb-5">
        <div
          v-for="option in sortByPosition(optionGroup.options)"
          :key="option.id"
          data-testid="product-variant"
        >
          <template v-if="optionGroup.displayType === 'color'">
            <div
              class="aspect-square w-12 text-sm border border-solid border-black03 relative before:border-pink flex items-center justify-center"
              :style="{ backgroundColor: option.colorHexCode }"
              :class="{
                'border-2 border-black': isOptionSelected(option.id),
              }"
              @click="handleChange(optionGroup.name, option.id, onHandleChange)"
            ></div>
            <p
              :id="`${option.id}-choice-label`"
              data-testid="product-variant-text"
              class="text-xs mt-2 text-center"
              :class="{ 'font-bold': isOptionSelected(option.id) }"
            >
              {{ option.name }}
            </p>
          </template>
          <template v-else>
            <label
              class="text-sm p-3 border border-solid border-black03 relative before:border-pink flex items-center justify-center cursor-pointer"
              :class="{
                'bg-black text-white': isOptionSelected(option.id),
              }"
              @click="handleChange(optionGroup.name, option.id, onHandleChange)"
            >
              <p
                :id="`${option.id}-choice-label`"
                data-testid="product-variant-text"
              >
                {{ option.name }}
              </p>
            </label>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
